/* Product-tag package selector. Prices shown here must be enforced by a Shopify discount or app. */
(() => {
class PackageBuilder extends HTMLElement {
  connectedCallback() {
    const node = this.querySelector('[data-package-builder-config]');
    if (!node) return;
    try { this.config = JSON.parse(node.textContent); } catch (error) {
      console.warn('Package builder configuration could not be read.', error);
      return;
    }
    this.offers = this.getOffers(this.config.tags || []);
    if (!this.offers.length || !this.config.product?.variants?.length) return;
    this.product = this.config.product;
    this.labels = this.getLabels();
    this.productInfo = this.closest('.product-information, product-info, [data-testid="product-information"]');
    this.variantPicker = this.productInfo?.querySelector('variant-picker, variant-selects') || null;
    this.form = this.productInfo?.querySelector('form[data-type="add-to-cart-form"]');
    if (!this.form) return;
    this.hasOnlyDefaultVariant = Boolean(this.config.hasOnlyDefaultVariant);
    this.nativeOptions = this.variantPicker ? [...this.variantPicker.querySelectorAll('.variant-option, .product-form__input')] : [];
    this.activeOffer = this.offers[0];
    this.boundSubmit = this.onSubmit.bind(this);
    this.boundFormClick = this.onFormClick.bind(this);
    this.addEventListener('click', this.onClick.bind(this));
    this.form.addEventListener('submit', this.boundSubmit, true);
    this.form.addEventListener('click', this.boundFormClick, true);
    this.render();
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.boundSubmit, true);
    this.form?.removeEventListener('click', this.boundFormClick, true);
  }

  getOffers(tags) {
    return tags.map((tag) => {
      const match = String(tag).trim().match(/^bundle:(\d+):(\d+(?:\.\d+)?)(?::(\d+))?$/i);
      if (!match) return null;
      return { quantity: Number(match[1]), price: Number(match[2]), selectionCount: Number(match[3] || match[1]) };
    }).filter((offer) => offer && offer.quantity > 0 && offer.selectionCount > 0 && offer.price >= 0)
      .sort((a, b) => a.quantity - b.quantity);
  }

  getLabels() {
    const lang = String(this.config.locale || document.documentElement.lang || 'en').toLowerCase().split('-')[0];
    const labels = {
      ro: ['Pachet', 'Articol', 'BUC.', 'Articol pachet', 'Alegeți o opțiune disponibilă pentru fiecare articol.'],
      es: ['Paquete', 'Artículo', 'UDS.', 'Artículo del paquete', 'Elija una opción disponible para cada artículo.'],
      fr: ['Lot', 'Article', 'PCS', 'Article du lot', 'Choisissez une option disponible pour chaque article.'],
      de: ['Paket', 'Artikel', 'STK.', 'Paketartikel', 'Wählen Sie für jeden Artikel eine verfügbare Option.'],
      it: ['Pacchetto', 'Articolo', 'PZ.', 'Articolo del pacchetto', 'Scegli un’opzione disponibile per ogni articolo.'],
      pt: ['Pacote', 'Artigo', 'UN.', 'Artigo do pacote', 'Escolha uma opção disponível para cada artigo.'],
      pl: ['Pakiet', 'Produkt', 'SZT.', 'Produkt w pakiecie', 'Wybierz dostępną opcję dla każdego produktu.'],
      zh: ['套餐', '商品', '件', '套餐商品', '请为每件商品选择可售规格。'],
      en: ['Package', 'Item', 'PCS', 'Package item', 'Please choose an available option for every item.']
    }[lang] || ['Package', 'Item', 'PCS', 'Package item', 'Please choose an available option for every item.'];
    return { package: labels[0], item: labels[1], unit: labels[2], packageItem: labels[3], chooseAvailable: labels[4] };
  }

  money(value) {
    try {
      return new Intl.NumberFormat(this.config.locale || document.documentElement.lang || 'en', {
        style: 'currency', currency: this.config.currency || 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0
      }).format(value);
    } catch (_) { return `${value} ${this.config.currency || ''}`.trim(); }
  }

  offerLabel(offer) { return `${offer.quantity} ${this.labels.unit}`; }
  get startingVariant() { return this.product.variants.find((variant) => variant.available) || this.product.variants[0]; }

  render() {
    this.hidden = false;
    this.innerHTML = `
      <fieldset class="package-picker" aria-label="${this.labels.package}">
        <legend>${this.labels.package}</legend>
        <div class="package-picker__offers">${this.offers.map((offer) => `
          <button type="button" class="package-card${offer === this.activeOffer ? ' is-selected' : ''}"
            data-package-index="${this.offers.indexOf(offer)}" aria-pressed="${offer === this.activeOffer}">
            <span class="package-card__radio" aria-hidden="true"></span>
            <span class="package-card__title">${this.offerLabel(offer)}</span>
            <span class="package-card__price">${this.money(offer.price)}</span>
          </button>`).join('')}</div>
      </fieldset>
      <div class="package-builder__components"${this.activeOffer.quantity === 1 || this.hasOnlyDefaultVariant ? ' hidden' : ''}></div>`;
    this.setMode();
  }

  setMode() {
    const multi = this.activeOffer.quantity > 1;
    this.nativeOptions.forEach((option) => option.classList.toggle('package-builder__native-options-hidden', multi));
    this.form.closest('product-form-component, product-form')?.classList.toggle('package-builder-active', multi);
    if (multi && !this.hasOnlyDefaultVariant) this.renderComponents();
  }

  renderComponents() {
    const defaults = this.startingVariant.options || [];
    this.querySelector('.package-builder__components').innerHTML = Array.from({ length: this.activeOffer.selectionCount }, (_, itemIndex) => `
      <fieldset class="package-builder__component">
        <legend>${this.labels.item} ${itemIndex + 1}</legend>
        ${this.product.options.map((name, optionIndex) => `
          <div class="package-builder__option">
            <span class="package-builder__option-label">${this.escape(name)}</span>
            <div class="package-builder__values">${[...new Set(this.product.variants.map((variant) => variant.options[optionIndex]))]
              .map((value) => `<button type="button" class="package-builder__value${value === defaults[optionIndex] ? ' is-selected' : ''}" data-value="${this.escape(value)}">${this.escape(value)}</button>`).join('')}</div>
          </div>`).join('')}
      </fieldset>`).join('');
  }

  escape(value) { const span = document.createElement('span'); span.textContent = value; return span.innerHTML; }

  onClick(event) {
    const offer = event.target.closest('[data-package-index]');
    if (offer) { this.activeOffer = this.offers[Number(offer.dataset.packageIndex)]; this.render(); return; }
    const value = event.target.closest('.package-builder__value');
    if (value) {
      value.parentElement.querySelectorAll('.package-builder__value').forEach((button) => button.classList.remove('is-selected'));
      value.classList.add('is-selected');
    }
  }

  selectedVariants() {
    if (this.hasOnlyDefaultVariant) return Array.from({ length: this.activeOffer.selectionCount }, () => this.startingVariant);
    return [...this.querySelectorAll('.package-builder__component')].map((component) => {
      const selected = [...component.querySelectorAll('.package-builder__option')]
        .map((option) => option.querySelector('.is-selected')?.dataset.value);
      return this.product.variants.find((variant) => variant.available && variant.options.every((value, index) => value === selected[index]));
    });
  }

  onSubmit(event) {
    if (this.activeOffer.quantity === 1) return;
    event.preventDefault(); event.stopImmediatePropagation(); this.addPackage(false);
  }

  onFormClick(event) {
    if (this.activeOffer.quantity === 1) return;
    if (!event.target.closest('.shopify-payment-button, shopify-accelerated-checkout, .accelerated-checkout-block')) return;
    event.preventDefault(); event.stopImmediatePropagation(); this.addPackage(true);
  }

  async addPackage(goToCheckout) {
    const variants = this.selectedVariants();
    if (variants.some((variant) => !variant)) { window.alert(this.labels.chooseAvailable); return; }
    const packageId = `bundle-${Date.now()}`;
    const items = variants.map((variant, index) => ({
      id: variant.id, quantity: 1,
      properties: { [this.labels.package]: this.offerLabel(this.activeOffer), [this.labels.packageItem]: `${index + 1} / ${this.activeOffer.selectionCount}`, _package_id: packageId }
    }));
    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items })
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || 'Package could not be added.');
      window.location.href = goToCheckout ? '/checkout' : Theme.routes.cart_url;
    } catch (error) { console.error(error); window.alert(error.message || 'Package could not be added.'); }
  }
}

if (!customElements.get('package-builder')) customElements.define('package-builder', PackageBuilder);
})();
